import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import Topbar from '../../components/shared/Topbar';
import TenantSidebar from '../../components/tenant/TenantSidebar';
import LegalFooter from '../../components/shared/LegalFooter';
import '../../styles/global.css';

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

export default function TenantSupport() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const loadTickets = () => {
    api.get('/tenant/support')
      .then(res => setTickets(res.data.tickets))
      .catch(() => toast.error('Could not load tickets'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadTickets(); }, []);

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      await api.post('/tenant/support', data);
      toast.success('Support ticket submitted. We will get back to you shortly.');
      reset();
      loadTickets();
    } catch {
      toast.error('Could not submit ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div className="app-layout">
      <Topbar role="tenant" />
      <div className="dashboard-layout">
        <TenantSidebar />
        <main id="main-content" className="main-content">
          <div className="page-title">Support</div>
          <div className="page-sub">Submit a request or track your existing tickets.</div>

          <div className="two-col">
            {/* New ticket form */}
            <div className="card" style={{ marginBottom: 0 }}>
              <div className="card-header"><div className="card-title">Submit a request</div></div>
              <div className="card-body">
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="form-group">
                    <label className="form-label">Subject</label>
                    <input className={`form-input${errors.subject ? ' error' : ''}`}
                      placeholder="Brief description of your issue"
                      {...register('subject', { required: true })} />
                    {errors.subject && <div className="form-error">Required</div>}
                  </div>
                  <div className="form-group">
                    <label className="form-label">Category</label>
                    <select className="form-input" {...register('category')}>
                      <option value="billing">Billing</option>
                      <option value="service">Internet service</option>
                      <option value="account">Account</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Description</label>
                    <textarea className={`form-input${errors.description ? ' error' : ''}`}
                      rows={5} placeholder="Please describe your issue in detail..."
                      {...register('description', { required: true })} />
                    {errors.description && <div className="form-error">Required</div>}
                  </div>
                  <button type="submit" className="btn btn-primary" style={{ width: '100%' }} disabled={submitting}>
                    {submitting ? 'Submitting...' : 'Submit Ticket'}
                  </button>
                </form>
              </div>
            </div>

            {/* Contact info */}
            <div>
              <div className="card" style={{ marginBottom: 16 }}>
                <div className="card-header"><div className="card-title">Contact us directly</div></div>
                <div className="card-body">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--text-sub)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Email</div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--navy)' }}>support@affordabletelecom.com</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: 'var(--text-sub)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Hours</div>
                      <div style={{ fontSize: 13, color: 'var(--text)' }}>Monday – Friday, 9am – 6pm ET</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Ticket history */}
          <div className="card" style={{ marginTop: 20 }}>
            <div className="card-header"><div className="card-title">My tickets</div></div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>Date</th><th>Subject</th><th>Category</th><th>Status</th></tr>
                </thead>
                <tbody>
                  {tickets.length === 0 && (
                    <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-sub)' }}>No tickets yet</td></tr>
                  )}
                  {tickets.map(t => (
                    <tr key={t.id}>
                      <td>{fmtDate(t.created_at)}</td>
                      <td style={{ fontWeight: 600 }}>{t.subject}</td>
                      <td style={{ textTransform: 'capitalize' }}>{t.category}</td>
                      <td>
                        <span className={`badge badge-${t.status === 'resolved' ? 'green' : t.status === 'open' ? 'blue' : 'orange'}`}>
                          {t.status.replace('_', ' ')}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <LegalFooter />
        </main>
      </div>
    </div>
  );
}
